import { z } from 'zod'
import { DateStringSchema, RegistrationSchema } from './booking'
import { daysUntilExpiry } from './aircraftDocuments'
import { EXPIRY_WARNING_DAYS } from './licences'

// The inventory: the club's spare parts and other stock (issue #187). Keep it
// in step with V1270-V1320 in db/flyway/schema/sql.
//
// An ITEM is what a thing is (an oil filter, part number CH48110-1); a LOT is
// what the club physically holds of it (3 bought in March at 24.50 EUR, on
// shelf A2). Price, currency, expiry, serial, location and quantity belong to
// the lot. A CATEGORY is a list the club keeps ("Aircraft parts"); every
// category shares the same two tables.
//
// The rules below are pure and live here, not in the API, because the
// maintenance site runs them on the tablet too.

export const STOCK_CONDITIONS = ['new', 'serviceable', 'overhauled', 'unserviceable', 'scrap'] as const
export type StockCondition = (typeof STOCK_CONDITIONS)[number]

// A part in one of these can be fitted. Unserviceable and scrap stock is
// kept on record but is not "in stock".
export const USABLE_CONDITIONS: readonly StockCondition[] = ['new', 'serviceable', 'overhauled']

export const DEFAULT_CURRENCY = 'EUR'
export const DEFAULT_UNIT = 'pcs'

// ---------------------------------------------------------------------------
// Shapes the API returns

export interface InventoryCategory {
  categoryKey: string
  nameFi: string
  nameSv: string | null
  nameEn: string | null
  aircraftRelated: boolean
  sortOrder: number
  archivedAt: string | null
}

export interface InventoryLocation {
  locationId: string
  name: string
  notes: string
  archivedAt: string | null
}

export interface InventoryFitment {
  fitmentId: string
  // Exactly one of the two is set.
  aircraftType: string | null
  registration: string | null
}

// What a file attached to a lot is, so the certificate can be found among the
// photos. The bytes stay in R2; the API serves them at
// /api/inventory/attachments/:id.
export const ATTACHMENT_KINDS = ['certificate', 'invoice', 'photo', 'other'] as const
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number]

export interface InventoryAttachment {
  attachmentId: string
  lotId: string
  kind: AttachmentKind
  filename: string
  contentType: string
  uploadedAt: string
}

export interface InventoryLot {
  lotId: string
  itemId: string
  locationId: string | null
  locationName: string | null
  quantity: number
  serialNumber: string | null
  batchNumber: string | null
  condition: StockCondition
  purchasedOn: string | null
  supplier: string
  reference: string
  unitPrice: number | null
  currency: string
  expiresOn: string | null
  notes: string
  createdAt: string
  attachments: InventoryAttachment[]
}

export interface InventoryItem {
  itemId: string
  categoryKey: string
  name: string
  description: string
  partNumber: string | null
  manufacturer: string
  unit: string
  minQuantity: number | null
  tags: string[]
  notes: string
  archivedAt: string | null
  createdAt: string
  fitments: InventoryFitment[]
  lots: InventoryLot[]
}

// One use of stock: units taken out of a lot, optionally for an aircraft.
export interface InventoryMovement {
  movementId: string
  lotId: string
  itemId: string
  itemName: string
  partNumber: string | null
  unit: string
  quantity: number
  registration: string | null
  maintenanceRecordId: number | null
  note: string
  usedBy: string | null
  usedByName: string | null
  usedAt: string
}

// ---------------------------------------------------------------------------
// Tags

const MAX_TAGS = 20
const MAX_TAG_LENGTH = 30

// A tag as stored: trimmed, lower-case, inner whitespace collapsed. "Oil
// Filter " and "oil  filter" are one tag, so a search for either finds both.
export function normaliseTag(tag: string): string {
  return tag.trim().replace(/\s+/g, ' ').toLowerCase().slice(0, MAX_TAG_LENGTH)
}

export function normaliseTags(tags: readonly string[]): string[] {
  const seen = new Set<string>()
  for (const tag of tags) {
    const normal = normaliseTag(tag)
    if (normal) seen.add(normal)
  }
  return [...seen].slice(0, MAX_TAGS)
}

// ---------------------------------------------------------------------------
// Rules

// Whole units of an item that could be fitted today: usable condition and not
// past the expiry date. `today` is the club's date, as for the licence rules.
export function usableQuantity(
  lots: readonly Pick<InventoryLot, 'quantity' | 'condition' | 'expiresOn'>[],
  today: string,
): number {
  return lots.reduce((sum, lot) => (isUsableLot(lot, today) ? sum + lot.quantity : sum), 0)
}

// "Running low": a reorder level is kept and the usable stock is at or under
// it. An item with no level is never low.
export function isLowStock(usable: number, minQuantity: number | null): boolean {
  return minQuantity !== null && usable <= minQuantity
}

// The soonest expiry among lots that still hold stock, or null.
export function nextExpiry(lots: readonly Pick<InventoryLot, 'quantity' | 'expiresOn'>[]): string | null {
  const dates = lots.filter((l) => l.quantity > 0 && l.expiresOn).map((l) => l.expiresOn as string)
  return dates.length ? dates.sort()[0]! : null
}

// Does the lot expire within `days` of `today` (or already have)? Lots with no
// stock left do not count: an empty lot is history.
export function lotExpiresWithin(
  lot: Pick<InventoryLot, 'quantity' | 'expiresOn'>,
  today: string,
  days = EXPIRY_WARNING_DAYS,
): boolean {
  return lot.quantity > 0 && lot.expiresOn !== null && daysUntilExpiry(lot.expiresOn, today) <= days
}

// Does an item fit this aircraft? No fitments means it fits anything (oil, a
// torch, a headset); otherwise a fitment must name the registration or the
// aircraft's type (case-insensitive: "PA-28-161" and "pa-28-161" are one).
export function fitsAircraft(
  fitments: readonly Pick<InventoryFitment, 'aircraftType' | 'registration'>[],
  aircraft: { registration: string; aircraftType: string | null },
): boolean {
  if (fitments.length === 0) return true
  const type = aircraft.aircraftType?.trim().toLowerCase()
  return fitments.some(
    (f) =>
      (f.registration !== null && f.registration === aircraft.registration) ||
      (f.aircraftType !== null && type !== undefined && f.aircraftType.trim().toLowerCase() === type),
  )
}

// What the usable stock cost, per currency. Never one figure: money in two
// currencies is not summed. Lots with no price are left out.
export function stockValueByCurrency(
  lots: readonly Pick<InventoryLot, 'quantity' | 'unitPrice' | 'currency' | 'condition'>[],
): Record<string, number> {
  const value: Record<string, number> = {}
  for (const lot of lots) {
    if (lot.unitPrice === null || lot.quantity === 0 || !USABLE_CONDITIONS.includes(lot.condition)) continue
    // Cents, so 0.1 + 0.2 style float noise cannot reach the screen.
    const cents = Math.round(lot.unitPrice * 100) * lot.quantity
    value[lot.currency] = (value[lot.currency] ?? 0) + cents / 100
  }
  return value
}

// The lot to take from first: usable and holding stock, soonest expiry first
// (stock that will lapse is used before stock that will not), then the oldest.
// What the maintenance site's one-tap "Use" picks.
export function lotToUse<T extends Pick<InventoryLot, 'quantity' | 'condition' | 'expiresOn' | 'createdAt'>>(
  lots: readonly T[],
  today: string,
): T | undefined {
  return lots
    .filter((lot) => lot.quantity > 0 && isUsableLot(lot, today))
    .sort((a, b) => {
      if (a.expiresOn && b.expiresOn && a.expiresOn !== b.expiresOn) return a.expiresOn.localeCompare(b.expiresOn)
      if (a.expiresOn !== b.expiresOn) return a.expiresOn ? -1 : 1
      return a.createdAt.localeCompare(b.createdAt)
    })[0]
}

// What a list row shows about an item beyond its own fields: computed once
// here so the API, admin and the maintenance tablet cannot disagree.
export interface InventoryItemSummary extends InventoryItem {
  usableQuantity: number
  lowStock: boolean
  nextExpiry: string | null
  expiring: boolean
  valueByCurrency: Record<string, number>
}

export function summariseItem(item: InventoryItem, today: string): InventoryItemSummary {
  const usable = usableQuantity(item.lots, today)
  return {
    ...item,
    usableQuantity: usable,
    lowStock: isLowStock(usable, item.minQuantity),
    nextExpiry: nextExpiry(item.lots),
    expiring: item.lots.some((lot) => lotExpiresWithin(lot, today)),
    valueByCurrency: stockValueByCurrency(item.lots),
  }
}

// ---------------------------------------------------------------------------
// Request contracts

const text = (max: number) => z.string().trim().max(max)
// A blank optional text field means "none": the form sends '' and the column
// is NULL.
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null))
const optionalDate = DateStringSchema.nullish().transform((v) => v ?? null)

export const CategoryKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Lower-case letters, digits and underscores, starting with a letter')

export const CreateCategorySchema = z.object({
  categoryKey: CategoryKeySchema,
  nameFi: text(80).min(1),
  nameSv: optionalText(80),
  nameEn: optionalText(80),
  aircraftRelated: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
})

// The key is the identity: it is not editable, only the rest.
export const UpdateCategorySchema = z
  .object({
    nameFi: text(80).min(1),
    nameSv: optionalText(80),
    nameEn: optionalText(80),
    aircraftRelated: z.boolean(),
    sortOrder: z.number().int().min(0).max(10_000),
    archived: z.boolean(),
  })
  .partial()

export const CreateLocationSchema = z.object({
  name: text(80).min(1),
  notes: text(500).default(''),
})

export const UpdateLocationSchema = z
  .object({
    name: text(80).min(1),
    notes: text(500),
    archived: z.boolean(),
  })
  .partial()

export const FitmentInputSchema = z.union([
  z.object({ aircraftType: text(60).min(1), registration: z.undefined().optional() }),
  z.object({ registration: RegistrationSchema, aircraftType: z.undefined().optional() }),
])
export type FitmentInput = z.infer<typeof FitmentInputSchema>

const LotFields = {
  locationId: z.string().uuid().nullish().transform((v) => v ?? null),
  quantity: z.number().int().min(0).max(1_000_000),
  serialNumber: optionalText(80),
  batchNumber: optionalText(80),
  condition: z.enum(STOCK_CONDITIONS),
  purchasedOn: optionalDate,
  supplier: text(120),
  reference: text(80),
  unitPrice: z.number().min(0).max(100_000_000).nullish().transform((v) => v ?? null),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'A 3-letter currency code, e.g. EUR'),
  expiresOn: optionalDate,
  notes: text(4000),
}

// A serialised part is one piece (the database CHECK says the same).
const serialIsOnePiece = (lot: { serialNumber?: string | null; quantity?: number }) =>
  !lot.serialNumber || lot.quantity === undefined || lot.quantity <= 1
const SERIAL_MESSAGE = { message: 'A part with a serial number is a single piece', path: ['quantity'] }

export const CreateLotSchema = z
  .object({
    ...LotFields,
    locationId: LotFields.locationId.default(null),
    quantity: LotFields.quantity.default(1),
    serialNumber: LotFields.serialNumber.default(null),
    batchNumber: LotFields.batchNumber.default(null),
    condition: LotFields.condition.default('new'),
    purchasedOn: LotFields.purchasedOn.default(null),
    supplier: LotFields.supplier.default(''),
    reference: LotFields.reference.default(''),
    unitPrice: LotFields.unitPrice.default(null),
    currency: LotFields.currency.default(DEFAULT_CURRENCY),
    expiresOn: LotFields.expiresOn.default(null),
    notes: LotFields.notes.default(''),
  })
  .refine(serialIsOnePiece, SERIAL_MESSAGE)
export type CreateLotInput = z.input<typeof CreateLotSchema>

// A partial update cannot see the stored quantity, so the API checks the
// serial rule against the merged row; here only a request that sets both.
export const UpdateLotSchema = z.object(LotFields).partial().refine(serialIsOnePiece, SERIAL_MESSAGE)

// Take stock out of a lot for use. The units are whole, and the lot must hold
// them: the API refuses to go below zero.
export const UseStockSchema = z.object({
  quantity: z.number().int().min(1).max(1_000_000),
  registration: RegistrationSchema.nullish().transform((v) => v ?? null),
  maintenanceRecordId: z.number().int().positive().nullish().transform((v) => v ?? null),
  note: text(500).default(''),
})
export type UseStockInput = z.input<typeof UseStockSchema>

// Stock that may be used today: a usable condition and not past its expiry.
export function isUsableLot(lot: Pick<InventoryLot, 'condition' | 'expiresOn'>, today: string): boolean {
  return USABLE_CONDITIONS.includes(lot.condition) && !(lot.expiresOn && lot.expiresOn < today)
}

export const CreateItemSchema = z.object({
  categoryKey: CategoryKeySchema,
  name: text(120).min(1),
  description: text(2000).default(''),
  partNumber: optionalText(80),
  manufacturer: text(80).default(''),
  unit: text(16).min(1).default(DEFAULT_UNIT),
  minQuantity: z.number().int().min(0).max(1_000_000).nullish().transform((v) => v ?? null),
  tags: z.array(z.string()).max(50).default([]).transform(normaliseTags),
  notes: text(4000).default(''),
  fitments: z.array(FitmentInputSchema).max(50).default([]),
  // "Add part" in the UI creates the item and its first lot in one go.
  lot: CreateLotSchema.optional(),
})
export type CreateItemInput = z.input<typeof CreateItemSchema>

export const UpdateItemSchema = z
  .object({
    categoryKey: CategoryKeySchema,
    name: text(120).min(1),
    description: text(2000),
    partNumber: optionalText(80),
    manufacturer: text(80),
    unit: text(16).min(1),
    minQuantity: z.number().int().min(0).max(1_000_000).nullable(),
    tags: z.array(z.string()).max(50).transform(normaliseTags),
    notes: text(4000),
    fitments: z.array(FitmentInputSchema).max(50),
    archived: z.boolean(),
  })
  .partial()

export const InventoryListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  category: z.string().trim().optional(),
  tag: z.string().trim().optional(),
  locationId: z.string().uuid().optional(),
  // Parts that fit this aircraft: its registration, its type, or anything.
  registration: z.string().trim().toUpperCase().optional(),
  expiring: z.enum(['true', 'false']).optional(),
  lowStock: z.enum(['true', 'false']).optional(),
  archived: z.enum(['true', 'false']).optional(),
})
export type InventoryListQuery = z.infer<typeof InventoryListQuerySchema>

// ---------------------------------------------------------------------------
// CSV export

// One cell, quoted when it needs to be. A cell that starts with = + - @ (or a
// tab or return) would be run as a formula by a spreadsheet, so it is
// prefixed with an apostrophe: part numbers and notes are typed by people.
export function csvCell(value: string | number | null | undefined): string {
  let text = value === null || value === undefined ? '' : String(value)
  if (/^[=+\-@\t\r]/.test(text) && typeof value === 'string') text = `'${text}`
  return /[",\n\r;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export const INVENTORY_CSV_COLUMNS = [
  'category',
  'name',
  'partNumber',
  'manufacturer',
  'tags',
  'fits',
  'quantity',
  'unit',
  'condition',
  'location',
  'serialNumber',
  'batchNumber',
  'purchasedOn',
  'supplier',
  'reference',
  'unitPrice',
  'currency',
  'expiresOn',
  'archived',
] as const

// The inventory as CSV, one row per stock lot (an item with no stock gets one
// row with an empty quantity), for a spreadsheet or an auditor. Comma
// separated with CRLF line ends and a header row.
export function inventoryToCsv(items: readonly InventoryItem[]): string {
  const rows: string[] = [INVENTORY_CSV_COLUMNS.join(',')]
  for (const item of items) {
    const base = [
      item.categoryKey,
      item.name,
      item.partNumber,
      item.manufacturer,
      item.tags.join(' '),
      item.fitments.map((f) => f.registration ?? f.aircraftType).join(' '),
    ]
    const lots: Array<InventoryLot | null> = item.lots.length ? [...item.lots] : [null]
    for (const lot of lots) {
      rows.push(
        [
          ...base,
          lot?.quantity ?? '',
          item.unit,
          lot?.condition ?? '',
          lot?.locationName ?? '',
          lot?.serialNumber ?? '',
          lot?.batchNumber ?? '',
          lot?.purchasedOn ?? '',
          lot?.supplier ?? '',
          lot?.reference ?? '',
          lot?.unitPrice ?? '',
          lot ? lot.currency : '',
          lot?.expiresOn ?? '',
          item.archivedAt ? 'yes' : '',
        ]
          .map((cell) => csvCell(cell as string | number | null))
          .join(','),
      )
    }
  }
  return `${rows.join('\r\n')}\r\n`
}
